import { IconFlask } from '@tabler/icons-react'

import { Badge } from '@/components/sui/badge'
import { Button } from '@/components/sui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/sui/tooltip'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useContextStore } from '@/stores/context'
import type { SandboxSource } from '@/stores/sandbox-dialog'
import { useSandboxDialogStore } from '@/stores/sandbox-dialog'

import locales from './locales'
import { trackSandboxBannerClicked } from './sandbox-banner.tracking'

interface SandboxBannerProps {
  collapsed?: boolean
  source: SandboxSource
}

export function SandboxBanner({
  collapsed = false,
  source,
}: SandboxBannerProps) {
  const { t } = useComponentTranslation(locales)
  const openDialog = useSandboxDialogStore((s) => s.openDialog)

  const userRole: 'Administrator' | 'User' = useContextStore((s) =>
    s.userAccountContext?.userAccountInformation.role === 'Administrator'
      ? 'Administrator'
      : 'User',
  )

  const currentPlan = useContextStore(
    (s) => s.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const isManaged = useContextStore(
    (s) => s.userAccountContext?.accountInformation.isManaged ?? false,
  )

  const handleClick = () => {
    trackSandboxBannerClicked({ source, userRole, currentPlan, isManaged })
    openDialog(source)
  }

  if (collapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                size="icon"
                onClick={handleClick}
                className="h-10 w-full bg-orange-600 text-white hover:bg-orange-700 focus-visible:border-orange-600 focus-visible:ring-orange-600/50 dark:bg-orange-700 dark:hover:bg-orange-800 dark:focus-visible:border-orange-700 dark:focus-visible:ring-orange-700/50"
              />
            }
          >
            <IconFlask className="h-5 w-5" />
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{t('bannerTitle')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-gradient-to-br from-orange-600 to-orange-600/65 p-3 text-white dark:from-orange-700 dark:to-orange-700/65">
      <div className="flex items-center gap-2">
        <div className="flex size-5 shrink-0 items-center justify-center rounded-md bg-white/15">
          <IconFlask className="size-3" />
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
          <span className="truncate text-sm font-semibold">
            {t('bannerTitle')}
          </span>
          <Badge variant="inverse" size="xs">
            {t('activeBadge')}
          </Badge>
        </div>
      </div>

      <p className="text-xs leading-snug opacity-80">
        {t('bannerDescription')}
      </p>

      <Button
        size="sm"
        onClick={handleClick}
        className="mt-1 w-full bg-white text-orange-600 hover:bg-white/90 focus-visible:border-white focus-visible:ring-white/50 dark:bg-orange-100 dark:text-orange-900 dark:hover:bg-orange-50 dark:focus-visible:border-orange-100 dark:focus-visible:ring-orange-100/50"
      >
        {t('learnMore')}
      </Button>
    </div>
  )
}
